import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button } from "src/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "src/core/components/ui/dialog";
import { useSendToValidation } from "src/core/hooks/folder/useSendToValidation";
import { useRequiredTenantPaths } from "src/core/hooks/routing/useTenantPaths";
import { toast } from "src/core/hooks/use-toast";

type ValidationModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
};

export default function ValidationModal({ open, setOpen }: ValidationModalProps) {
  const { t } = useTranslation();
  const paths = useRequiredTenantPaths();
  const navigate = useNavigate();

  const { validate, handleCancel, isLoading } = useSendToValidation(setOpen);

  const handleConfirm = async () => {
    const { status, folderFileId, redirect } = await validate();

    if (status === "success") {
      if (redirect) {
        if (!folderFileId) return;

        toast({
          description: (
            <div className="space-y-1">
              <span>{t("folder.validation.success")}</span>
              <a href={paths.folder(folderFileId)} className="underline hover:cursor-pointer mx-5">
                {t("global.seeFolder")}
              </a>
            </div>
          ),
        });

        navigate(paths.home());
      } else {
        toast({
          description: t("global.form.success"),
        });
      }
    } else {
      toast({
        description: t("global.form.fail"),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent hideCloseButton>
        <DialogHeader>
          <DialogTitle>{t("folder.validation.title")}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="whitespace-pre-line">
          {t("folder.validation.message")}
        </DialogDescription>
        <DialogFooter className="gap-4">
          <Button onClick={handleConfirm} loading={isLoading}>
            {t("folder.validation.confirm")}
          </Button>
          <Button variant="link" onClick={handleCancel} disabled={isLoading}>
            {t("global.form.cancel")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
